import { Injectable, Signal, computed, signal } from '@angular/core';

export type NewRowsNamespace = 'requests' | 'leads';

export interface PolledRow {
    id: string;
    isOpened?: boolean;
}

interface NamespaceState {
    seen: Set<string>;
    newSinceFirstPoll: Set<string>;
    firstPollDone: boolean;
}

@Injectable({ providedIn: 'root' })
export class NewRowsTrackerService {
    private readonly _state = new Map<NewRowsNamespace, NamespaceState>();
    private readonly _signals = new Map<NewRowsNamespace, ReturnType<typeof signal<Set<string>>>>();

    applyPoll(ns: NewRowsNamespace, items: readonly PolledRow[]): void {
        const state = this._ensureState(ns);

        if (!state.firstPollDone) {
            for (const item of items) state.seen.add(item.id);
            state.firstPollDone = true;
            return;
        }

        let changed = false;
        for (const item of items) {
            if (item.isOpened) {
                // Mirror BE: an already-opened row is never "new" — bring it
                // into the baseline and clear any prior new flag.
                if (!state.seen.has(item.id)) state.seen.add(item.id);
                if (state.newSinceFirstPoll.has(item.id)) {
                    state.newSinceFirstPoll.delete(item.id);
                    changed = true;
                }
                continue;
            }
            if (!state.seen.has(item.id)) {
                state.seen.add(item.id);
                state.newSinceFirstPoll.add(item.id);
                changed = true;
            }
        }
        if (changed) {
            this._ensureSignal(ns).set(new Set(state.newSinceFirstPoll));
        }
    }

    markOpened(ns: NewRowsNamespace, id: string): void {
        const state = this._state.get(ns);
        if (!state || !state.newSinceFirstPoll.has(id)) return;
        state.newSinceFirstPoll.delete(id);
        this._ensureSignal(ns).set(new Set(state.newSinceFirstPoll));
    }

    newIds(ns: NewRowsNamespace): Signal<Set<string>> {
        return this._ensureSignal(ns).asReadonly();
    }

    count(ns: NewRowsNamespace): Signal<number> {
        const sig = this._ensureSignal(ns);
        return computed(() => sig().size);
    }

    private _ensureState(ns: NewRowsNamespace): NamespaceState {
        let s = this._state.get(ns);
        if (!s) {
            s = { seen: new Set(), newSinceFirstPoll: new Set(), firstPollDone: false };
            this._state.set(ns, s);
        }
        return s;
    }

    private _ensureSignal(ns: NewRowsNamespace) {
        let sig = this._signals.get(ns);
        if (!sig) {
            sig = signal<Set<string>>(new Set());
            this._signals.set(ns, sig);
        }
        return sig;
    }
}
